import { useState, useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { db } from '../../firebase/config'
import firebase from '../../firebase/compat'
import { client } from '../../hooks/Client'
import { useFirestore, useFirestoreGeneralTwo } from '../../firebase/useFirestore'
import {
    DndContext,
    DragOverlay,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    rectSortingStrategy,
    horizontalListSortingStrategy,
} from '@dnd-kit/sortable'
import PageHeader from '../../components/layout/PageHeader'
import DashboardCard from '../../components/dashboard/DashboardCard'
import DashboardCustomizeModal from '../../components/dashboard/DashboardCustomizeModal'
import DashboardVisualisationCard from '../../components/dashboard/DashboardVisualisationCard'
import useStaticCards from '../../components/dashboard/useStaticCards'
import {
    STATIC_CARD_COMPONENTS,
    STATIC_CARD_CLASSES,
    isStaticCardVisible,
    getCardZone,
} from '../../components/dashboard/staticCardRegistry'
import useSettings from '../../hooks/Settings'
import resolveComparePeriods from '../../helpers/compareHelpers/resolveComparePeriods'
import { PortfolioHouseVisitsProvider } from '../../StateManagment/PortfolioHouseVisitsContext'
import { MeasurementSignalsProvider } from '../../StateManagment/MeasurementSignalsContext'
import softDeleteDoc from '../../helpers/softDeleteDoc';

// A dynamic card widens to span two grid columns while it's comparing two
// periods, so the two side-by-side charts stay readable. Reuses the existing
// `--wide` modifier (also used by some static cards). Static cards never
// carry a compare period. See docs/features/compare-two-periods.md.
const cardExtraClass = (item) => {
    const base = STATIC_CARD_CLASSES[item.Type] || ''
    const compareActive = !item.Static
        && resolveComparePeriods({ period: item.Period, compare: item.ComparePeriod }).compareActive
    return [base, compareActive ? 'new-dashboard-card--wide' : ''].filter(Boolean).join(' ')
}

/**
 * One self-contained drag universe: a DndContext + SortableContext + overlay
 * over a single set of cards. The KPI/trend row and the main grid each get
 * their own zone, so a card can only be reordered within its own row. Renders
 * nothing when the zone is empty.
 *
 * @param {object} props
 * @param {Array<object>} props.cards - cards in this zone, already in Position order
 * @param {object} props.strategy - dnd-kit sorting strategy (grid vs horizontal list)
 * @param {string} props.gridClassName - wrapper class for the zone's layout
 * @param {(reordered: Array<object>) => void} props.onReorder - called with the new card order
 * @param {(item: object) => React.ReactNode} props.renderCardContent - renders a card's body
 * @param {(item: object) => void} props.deleteHandler - deletes (soft/hard) a card
 */
const DashboardDragZone = ({ cards, strategy, gridClassName, onReorder, renderCardContent, deleteHandler }) => {
    const [activeId, setActiveId] = useState(null)

    // PointerSensor with an 8px activation distance so clicks on the delete
    // button don't start a drag. KeyboardSensor gives arrow-key reordering.
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    )

    const activeItem = activeId ? cards.find((c) => c.ID === activeId) || null : null

    const handleDragEnd = (event) => {
        setActiveId(null)
        const { active, over } = event
        if (!over || active.id === over.id) return
        const oldIndex = cards.findIndex((c) => c.ID === active.id)
        const newIndex = cards.findIndex((c) => c.ID === over.id)
        if (oldIndex === -1 || newIndex === -1) return
        onReorder(arrayMove(cards, oldIndex, newIndex))
    }

    if (cards.length === 0) return null

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(event) => setActiveId(event.active.id)}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveId(null)}
        >
            <SortableContext items={cards.map((c) => c.ID)} strategy={strategy}>
                <div className={gridClassName}>
                    {cards.map((item) => (
                        <DashboardCard
                            key={item.ID}
                            id={item.ID}
                            onDelete={() => deleteHandler(item)}
                            extraClass={cardExtraClass(item)}
                        >
                            {renderCardContent(item)}
                        </DashboardCard>
                    ))}
                </div>
            </SortableContext>

            <DragOverlay>
                {activeItem ? (
                    <DashboardCard
                        id={activeItem.ID}
                        extraClass={cardExtraClass(activeItem)}
                        isOverlay
                    >
                        {renderCardContent(activeItem)}
                    </DashboardCard>
                ) : null}
            </DragOverlay>
        </DndContext>
    )
}

const Dashboard = () => {
    const history = useHistory()
    const settings = useSettings()
    // Firestore — only Version 2 items
    const savedItems = useFirestoreGeneralTwo('DashboardItems', 'CompagnyID', client, 'Version', 2)
    // One listener shared by every DashboardVisualisationCard instead of N.
    // All statuses — the persona filter chip narrows by status itself.
    const personas = useFirestore('Personas')
    const personasEnabled = settings.personasOn === 'flex'

    // State
    const [dragArray, setDragArray] = useState([])
    const [customizeModalOpen, setCustomizeModalOpen] = useState(false)

    // Static card catalog (active + addable) for the customize modal
    const { staticCards, restoreCard } = useStaticCards(savedItems)

    // useEffect
    useEffect(() => {
        if (!savedItems) return
        const active = [...savedItems]
            .filter((item) => (item?.Deleted || "false") !== "true")
            .sort((a, b) => a.Position - b.Position)
        setDragArray(active)
    }, [savedItems])

    // Cards actually rendered to the DOM. Hidden cards (visibility-restricted
    // or missing static component) stay in dragArray but don't render.
    // AI-CONTEXT: See docs/pitfalls.md#ui-sortable-index-gap — filter BEFORE
    // the Sortable map so useSortable ids stay contiguous.
    const visibleCards = useMemo(() => {
        return dragArray.filter((item) => {
            if (item.Static && !STATIC_CARD_COMPONENTS[item.Type]) return false
            if (!isStaticCardVisible(item.Type, client, settings)) return false
            return true
        })
    }, [dragArray, settings])

    // Split into drag zones. Each is filtered from the Position-sorted
    // visibleCards, so cards stay in order within their own row.
    const kpiCards = useMemo(
        () => visibleCards.filter((c) => getCardZone(c.Type) === 'kpi'),
        [visibleCards],
    )
    const mainCards = useMemo(
        () => visibleCards.filter((c) => getCardZone(c.Type) !== 'kpi'),
        [visibleCards],
    )

    // Shared content renderer so the overlay can show the same body as the
    // sortable card without duplicating the static/dynamic branching.
    const renderCardContent = (item) => {
        if (item.Static) {
            const StaticComponent = STATIC_CARD_COMPONENTS[item.Type]
            return StaticComponent ? <StaticComponent item={item} /> : null
        }
        return (
            <DashboardVisualisationCard
                item={item}
                personas={personas}
                personasEnabled={personasEnabled}
            />
        )
    }

    // Reorders one zone's cards. Positions are renumbered per zone — the two
    // rows render from separate Position-sorted lists, so their ranges never
    // need to agree with each other.
    const reorderCards = (reordered) => {
        const newPosById = new Map(reordered.map((item, idx) => [item.ID, idx + 1]))
        setDragArray((prev) =>
            prev.map((item) =>
                newPosById.has(item.ID) ? { ...item, Position: newPosById.get(item.ID) } : item,
            ),
        )

        // Atomic batch so positions can't end up partially applied if the
        // page closes mid-update.
        const batch = db.batch()
        reordered.forEach((item, idx) => {
            batch.update(db.collection('DashboardItems').doc(item.docid), { Position: idx + 1 })
        })
        batch.commit()
    }

    const deleteHandler = (item) => {
        if (item.Static) {
            // Static cards: soft delete only (ADR-008). Schreef eerder de tekst
            // "true" zonder DeletedBy; via de gedeelde helper is dat een echte
            // boolean mét wie het deed, zodat een prullenbak ermee overweg kan.
            softDeleteDoc({ collection: 'DashboardItems', docid: item.docid })
        } else {
            // Dynamic cards: hard delete
            db.collection('DashboardItems').doc(item.docid).delete()
        }
    }

    const headerActions = (
        <button
            type='button'
            className='page-header-button page-header-button--outline'
            onClick={() => setCustomizeModalOpen(true)}
        >
            Aanpassen
        </button>
    )

    // Hoist the housevisits portfolio fan-out to a single shared call.
    // Provider is conditional so non-portfolio dashboards don't mount the
    // listener.
    // AI-CONTEXT: See docs/patterns.md#dashboard-portfolio-aggregation-card
    const isPortfolio = settings?.portfolio === 'block'
    const withPortfolioProviders = (children) =>
        isPortfolio ? (
            <PortfolioHouseVisitsProvider showDemoProjects={settings.showDemoProjects}>{children}</PortfolioHouseVisitsProvider>
        ) : (
            children
        )

    // Alleen ophalen als een kaart die erop leunt er ook echt staat. De
    // signalering leest de antwoorden van elk onderzoek; dat mag geen enkel
    // dashboard kosten dat er niets mee doet. Beide kaarten hangen aan dezelfde
    // provider — één ophaalactie, twee afnemers.
    const hasMeasurementSignalCard = useMemo(
        () => visibleCards.some((item) => item.Type === 'upcoming-moments' || item.Type === 'anonymous-research'),
        [visibleCards],
    )
    // De kaart 'Anonieme onderzoeken' kost een lees-ronde extra per onderzoek
    // dat géén deelnemer bereikt. Dat mag alleen een dashboard kosten dat de
    // kaart ook echt toont.
    const hasAnonymousResearchCard = useMemo(
        () => visibleCards.some((item) => item.Type === 'anonymous-research'),
        [visibleCards],
    )
    // De provider hangt er altijd, met een vlag erin. Hem voorwaardelijk mounten
    // leek zuiniger, maar `visibleCards` begint leeg en vult zich pas als de
    // kaarten binnen zijn: de voorwaarde klapt dus bij elke dashboardlading om
    // van false naar true. Dat wisselt het elementtype op die plek, waarna React
    // de hele boom eronder weggooit en opnieuw opbouwt — inclusief de
    // Firestore-reads van élke kaart erin.
    const withMeasurementSignals = (children) => (
        <MeasurementSignalsProvider enabled={hasMeasurementSignalCard} includeAnonymous={hasAnonymousResearchCard}>
            {children}
        </MeasurementSignalsProvider>
    )

    return (
        <div className='main'>
            <div className='main-container'>
                <PageHeader
                    heading='Dashboard'
                    description={null}
                    AI={false}
                    actions={headerActions}
                />
                {withPortfolioProviders(withMeasurementSignals(
                    <>
                        <DashboardDragZone
                            cards={kpiCards}
                            strategy={horizontalListSortingStrategy}
                            gridClassName='new-dashboard-kpi-row'
                            onReorder={reorderCards}
                            renderCardContent={renderCardContent}
                            deleteHandler={deleteHandler}
                        />
                        <DashboardDragZone
                            cards={mainCards}
                            strategy={rectSortingStrategy}
                            gridClassName='new-dashboard-grid new-dashboard-grid--main'
                            onReorder={reorderCards}
                            renderCardContent={renderCardContent}
                            deleteHandler={deleteHandler}
                        />
                    </>,
                ))}

                <DashboardCustomizeModal
                    modalOpen={customizeModalOpen}
                    setModalOpen={setCustomizeModalOpen}
                    staticCards={staticCards}
                    restoreCard={restoreCard}
                    showAnalysisLink={!!settings.impactFunctionality}
                    onGoToAnalysis={() => history.push(`/${client}/analysis`)}
                />
            </div>
        </div>
    )
}

export default Dashboard
